import React from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { MapPin, Phone, Mail } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations/translations';
import { contactInfo } from '../data/mockData';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Create red marker icon
const redIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const ContactSection = () => {
  const { language } = useLanguage();
  const t = translations[language];

  // Noble Law Firm office location in Erbil (Exact location: Minara Choli)
  const officeLocation = [36.189083, 43.998847];

  return (
    <section id="contact" className="py-20 bg-white relative overflow-hidden">
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, #c4a35a 1px, transparent 0)', backgroundSize: '40px 40px' }} />
      
      <div className="container mx-auto px-4 relative z-10">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-[#1a1d2e] mb-4">
            {t.contact}
          </h2>
          <div className="w-20 h-1 bg-[#c4a35a] mx-auto mb-6" />
        </div>

        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Contact Details Box */}
            <div className="bg-white border-2 border-[#c4a35a]/30 rounded-xl p-8 shadow-lg">
              <h3 className="text-2xl font-bold text-[#1a1d2e] mb-6">{t.office}</h3>
              
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#c4a35a]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-[#c4a35a]" />
                  </div>
                  <a 
                    href="https://www.google.com/maps?q=36.189083,43.998847"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:opacity-80 transition-opacity cursor-pointer"
                  >
                    <p className="text-[#1a1d2e] font-semibold text-lg hover:text-[#c4a35a] transition-colors">{t.location}</p>
                    <p className="text-gray-600">{t.locationDetail}</p>
                  </a>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#c4a35a]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-[#c4a35a]" />
                  </div>
                  <div className="space-y-2">
                    {contactInfo.phones.map((phone) => (
                      <a key={phone} href={`tel:${phone}`} className="block text-[#1a1d2e] hover:text-[#c4a35a] transition-colors text-lg font-medium">
                        {phone}
                      </a>
                    ))}
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#c4a35a]/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#c4a35a]" />
                  </div>
                  <a href={`mailto:${contactInfo.email}`} className="text-[#1a1d2e] hover:text-[#c4a35a] transition-colors text-lg font-medium">
                    {contactInfo.email}
                  </a>
                </div>
              </div>
            </div>

            {/* Interactive Map */}
            <div className="bg-white border-2 border-[#c4a35a]/30 rounded-xl overflow-hidden shadow-lg">
              <div className="h-full min-h-[400px] relative">
                <MapContainer
                  center={officeLocation}
                  zoom={15}
                  scrollWheelZoom={true}
                  className="h-full w-full"
                  style={{ minHeight: '400px', borderRadius: '0.75rem' }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={officeLocation} icon={redIcon}>
                    <Popup>
                      <div className="text-center p-2">
                        <strong className="text-[#1a1d2e] text-lg">Noble Law Firm</strong>
                        <p className="text-gray-600 text-sm mt-1">{t.location}</p>
                      </div>
                    </Popup>
                  </Marker>
                </MapContainer>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
